import React, { useEffect, useRef, useState } from 'react';
import { isTruthyAndNotEmpty } from '@stimcar/libs-kernel';
import * as m from '../paraglide/messages.js';
import type { SessionVisibility } from './api.js';
import { ChevronDownIcon, LockIcon, UnlockIcon } from './icons.js';

export type SessionSharingPanelProps = {
  readonly visibility: SessionVisibility;
  readonly sharedWithEmails: readonly string[];
  readonly canManage: boolean;
  readonly busy?: boolean;
  readonly onVisibilityChange: (visibility: SessionVisibility) => void;
  readonly onSharedWithEmailsChange: (emails: readonly string[]) => void;
};

export function SessionSharingPanel(props: SessionSharingPanelProps): React.ReactElement {
  const {
    visibility,
    sharedWithEmails,
    canManage,
    busy: busyProp,
    onVisibilityChange,
    onSharedWithEmailsChange,
  } = props;
  const busy = busyProp === true;
  const [menuOpen, setMenuOpen] = useState(false);
  const [draftEmail, setDraftEmail] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const isPrivate = visibility === 'private';
  const statusLabel = isPrivate ? m.sessionVisibilityPrivate() : m.sessionVisibilityOpen();

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    function onPointerDown(event: MouseEvent): void {
      const root = menuRef.current;
      if (root === null || root.contains(event.target as Node)) {
        return;
      }
      setMenuOpen(false);
    }
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  function addEmail(): void {
    const email = draftEmail.trim().toLowerCase();
    if (!isTruthyAndNotEmpty(email) || sharedWithEmails.includes(email)) {
      setDraftEmail('');
      return;
    }
    onSharedWithEmailsChange([...sharedWithEmails, email]);
    setDraftEmail('');
  }

  return (
    <div className="studio-session-sharing" ref={menuRef} aria-label={m.sessionSharing()}>
      <button
        type="button"
        className={
          isPrivate
            ? 'button button-secondary studio-session-sharing-trigger is-private'
            : 'button button-secondary studio-session-sharing-trigger is-open'
        }
        disabled={busy}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        title={m.sessionSharing()}
        onClick={() => {
          setMenuOpen((open) => !open);
        }}
      >
        {isPrivate ? <LockIcon /> : <UnlockIcon />}
        <span className="studio-session-sharing-trigger-label">{statusLabel}</span>
        <ChevronDownIcon />
      </button>
      {menuOpen ? (
        <div className="studio-session-sharing-menu" role="menu">
          <p className="studio-session-sharing-menu-status">
            {statusLabel}
            {!canManage && isPrivate && sharedWithEmails.length > 0
              ? ` — ${m.sessionSharedWithYou()}`
              : undefined}
          </p>
          {canManage ? (
            <>
              <button
                type="button"
                className="studio-session-sharing-menu-item"
                role="menuitem"
                disabled={busy}
                onClick={() => {
                  onVisibilityChange(isPrivate ? 'open' : 'private');
                }}
              >
                {isPrivate ? m.sessionMakeOpen() : m.sessionMakePrivate()}
              </button>
              {isPrivate ? (
                <div className="studio-session-sharing-invite">
                  <ul className="studio-session-sharing-list">
                    {sharedWithEmails.map((email) => (
                      <li key={email}>
                        <span>{email}</span>
                        <button
                          type="button"
                          className="button button-secondary"
                          disabled={busy}
                          aria-label={m.sessionUnshareEmail({ email })}
                          onClick={() => {
                            onSharedWithEmailsChange(
                              sharedWithEmails.filter((item) => item !== email)
                            );
                          }}
                        >
                          {m.sessionUnshare()}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="studio-session-sharing-add">
                    <input
                      type="email"
                      value={draftEmail}
                      disabled={busy}
                      placeholder={m.sessionShareEmailPlaceholder()}
                      aria-label={m.sessionShareEmailPlaceholder()}
                      onChange={(event) => {
                        setDraftEmail(event.target.value);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addEmail();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={busy}
                      onClick={addEmail}
                    >
                      {m.sessionShareAdd()}
                    </button>
                  </div>
                </div>
              ) : undefined}
            </>
          ) : undefined}
        </div>
      ) : undefined}
    </div>
  );
}
